import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useUser } from '@plato/app-core/hooks/use-user';
import TaskIcon from '@plato/ui-legacy/assets/icons/task-icon';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import Text from '@plato/ui-legacy/components/ui/text';
import { cn } from '@plato/ui/util/classes';
import * as Accordion from '@radix-ui/react-accordion';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarIcon, ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CrmEventDTO } from '../../../server/dto/crm-event-dto.ts';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import { getEventMetadata } from '../../../utils/event-utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';
import { EventDetailsDialog as NewEventDetailsDialog } from '../../events-v2/components/event-details-dialog.tsx';
import { EventDetailsDialog } from '../../events/components/event-details-dialog.tsx';
import { TimelineList } from '../../shared/components/timeline-list/timeline-list.tsx';
import { EventTimelineHeader } from './event-timeline-header.tsx';
import { OpenTaskTimelineBody } from './open-task-timeline-body.tsx';
import { OpenTaskTimelineHeader } from './open-task-timeline-header.tsx';

type Props = {
    isLoading: boolean;
    events: CrmEventDTO[];
    tasks: CrmTaskDTO[];
};

export function ActivityUpcomingTimeline({ isLoading, events, tasks }: Props) {
    const { t } = useTranslation('customer-detail');
    const user = useUser();
    const { openTaskDetails } = useDialogs();
    const sendEvent = useAnalytics();
    const { enableNewEventPage } = useAppConfig();

    const hasUpcoming = tasks.length > 0 || events.length > 0;

    return (
        <AnimatePresence>
            {hasUpcoming && (
                <motion.div
                    key={'open-tasks-accordion'}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                        duration: 0.2,
                        ease: ['easeIn', 'easeOut'],
                    }}
                >
                    <Accordion.Root type="multiple" defaultValue={['open-tasks']}>
                        <Accordion.Item value="open-tasks">
                            <Accordion.Trigger className="flex items-center group">
                                <div className="items-center justify-center h-6 w-6 ml-1 mr-2 flex">
                                    <ChevronDownIcon className="w-4 h-4 text-neutral-700 group-data-[state=open]:rotate-180" />
                                </div>
                                <Text variant="h1">
                                    {t('tasks__group_upcoming', {
                                        ns: 'tasks',
                                    })}
                                </Text>
                            </Accordion.Trigger>
                            <Accordion.Content>
                                <TimelineList isLoading={isLoading}>
                                    <TimelineList.Group>
                                        {events.length > 0 &&
                                            events.map((event) => (
                                                <TimelineList.Item
                                                    icon={CalendarIcon}
                                                    key={`events-${event.event_id}`}
                                                    onClick={
                                                        event.owner_id === user.id
                                                            ? () => {
                                                                  sendEvent(
                                                                      'event_clicked',
                                                                      getEventMetadata(event),
                                                                  );
                                                                  void DialogStateManager.show(
                                                                      enableNewEventPage
                                                                          ? NewEventDetailsDialog
                                                                          : EventDetailsDialog,
                                                                      {
                                                                          eventId: event.event_id,
                                                                          date: event.date,
                                                                          analyticsSource: 'customer_detail',
                                                                      },
                                                                  );
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    <TimelineList.ItemHeader
                                                        className={cn({
                                                            'border-b-0': !event.title,
                                                        })}
                                                    >
                                                        <EventTimelineHeader event={event} />
                                                    </TimelineList.ItemHeader>
                                                    {event.title && (
                                                        <TimelineList.ItemBody>
                                                            <Text>{event.title}</Text>
                                                        </TimelineList.ItemBody>
                                                    )}
                                                </TimelineList.Item>
                                            ))}
                                        {tasks.length > 0 &&
                                            tasks.map((task) => (
                                                <TimelineList.Item
                                                    icon={TaskIcon}
                                                    key={`tasks-${task.taskId}`}
                                                    onClick={() => {
                                                        openTaskDetails(task.taskId);
                                                    }}
                                                >
                                                    <TimelineList.ItemHeader>
                                                        <OpenTaskTimelineHeader task={task} />
                                                    </TimelineList.ItemHeader>
                                                    <TimelineList.ItemBody>
                                                        <OpenTaskTimelineBody
                                                            task={task}
                                                            source="customer_detail"
                                                        />
                                                    </TimelineList.ItemBody>
                                                </TimelineList.Item>
                                            ))}
                                    </TimelineList.Group>
                                </TimelineList>
                            </Accordion.Content>
                        </Accordion.Item>
                    </Accordion.Root>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
